<!--
  Flexi › Safety rounds: the latest in-progress and completed rounds as cards,
  then the full history. Starting and deleting are for authors only.
-->
<section class="fvr-card fvr-list" [attr.aria-busy]="loading">
  <header class="fvr-head fvr-list__head">
    <div>
      <h2 class="fvr-title">{{ "FLEXI_SAFETY_ROUNDS.TAB" | translate }}</h2>
      <p class="fvr-subtitle">
        {{ "FLEXI_SAFETY_ROUNDS.LIST.SUBTITLE" | translate }}
      </p>
    </div>
    @if (canAuthor) {
      <button
        type="button"
        class="fas-btn fas-btn--primary"
        [disabled]="loading || starting"
        (click)="openStart()"
      >
        <i class="pi pi-plus" aria-hidden="true"></i>
        <span>{{ "FLEXI_SAFETY_ROUNDS.LIST.NEW" | translate }}</span>
      </button>
    }
  </header>

  @if (loading) {
    <flexi-safety-round-skeleton layout="overview" />
  }

  @if (loadFailed) {
    <p class="fas-error" role="alert">
      {{ "FLEXI_SAFETY_ROUNDS.LOAD_FAILED" | translate }}
    </p>
    <button type="button" class="fas-btn fas-btn--ghost" (click)="load()">{{ 'FLEXI_SAFETY_ROUNDS.ASSIGN.RETRY' | translate }}</button>
  }

  @for (action of busyActions | keyvalue; track action.key) {
    <p class="fvr-action-status" role="status"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i>{{ action.value | translate }}</p>
  }
  @if (inProgress || lastCompleted) {
    <div class="fvr-list__cards">
      @if (inProgress; as round) {
        <article class="fvr-summary fvr-summary--active">
          <span class="fvr-eyebrow fvr-summary__eyebrow">{{
            "FLEXI_SAFETY_ROUNDS.STATUS.IN_PROGRESS" | translate
          }}</span>
          <div class="fvr-summary__title">
            {{ round.RoundDate | date: "dd-MM-yyyy" }}
            @if (round.Location) {
              · {{ round.Location }}
            }
          </div>
          <div class="fas-muted">
            {{
              round.TemplateName ||
                ("FLEXI_SAFETY_ROUNDS.NO_TEMPLATE" | translate)
            }}
            @if (round.ResponsibleUserName) {
              ·
              {{
                "FLEXI_SAFETY_ROUNDS.RESPONSIBLE_NAMED"
                  | translate: { name: round.ResponsibleUserName }
              }}
            }
          </div>
          <div class="fvr-progress">
            <div
              class="fvr-progress__track"
              role="progressbar"
              [attr.aria-valuemin]="0"
              [attr.aria-valuemax]="round.ItemCount"
              [attr.aria-valuenow]="round.GradedCount"
              [attr.aria-label]="
                'FLEXI_SAFETY_ROUNDS.PROGRESS_LABEL' | translate
              "
            >
              <div
                class="fvr-progress__fill"
                [style.width.%]="percentOf(round.GradedCount, round.ItemCount)"
              ></div>
            </div>
            <span class="fvr-progress__label">
              {{
                "FLEXI_SAFETY_ROUNDS.ANSWERED_OF"
                  | translate
                    : { graded: round.GradedCount, total: round.ItemCount }
              }}
            </span>
          </div>
          <div class="fvr-summary__foot">
            <div class="fvr-chips">
              @for (chip of findingChips(round); track chip.grade) {
                <span class="fvr-grade" [ngClass]="'fvr-grade--' + chip.grade">
                  {{
                    "FLEXI_SAFETY_ROUNDS.FINDING_CHIP"
                      | translate: { grade: chip.grade, count: chip.count }
                  }}
                </span>
              }
            </div>
            <button
              type="button"
              class="fas-btn fas-btn--primary"
              (click)="open(round)"
            >
              {{
                (canAuthor
                  ? "FLEXI_SAFETY_ROUNDS.LIST.CONTINUE"
                  : "FLEXI_SAFETY_ROUNDS.LIST.OPEN"
                ) | translate
              }}
            </button>
          </div>
        </article>
      }

      @if (lastCompleted; as round) {
        <article class="fvr-summary">
          <span class="fvr-eyebrow">{{
            "FLEXI_SAFETY_ROUNDS.LIST.LAST_COMPLETED" | translate
          }}</span>
          <div class="fvr-summary__title">
            <a class="fvr-link" href="javascript:void(0)" (click)="open(round)">
              {{ round.RoundDate | date: "dd-MM-yyyy" }}
              @if (round.Location) {
                · {{ round.Location }}
              }
            </a>
          </div>
          <div class="fvr-chips">
            @for (chip of findingChips(round); track chip.grade) {
              <span class="fvr-grade" [ngClass]="'fvr-grade--' + chip.grade">
                {{
                  "FLEXI_SAFETY_ROUNDS.FINDING_CHIP"
                    | translate: { grade: chip.grade, count: chip.count }
                }}
              </span>
            } @empty {
              <span class="fas-muted">{{
                "FLEXI_SAFETY_ROUNDS.LIST.NO_FINDINGS" | translate
              }}</span>
            }
          </div>
          @if (findingTotal(round) > 0) {
            <div class="fas-muted">
              {{
                "FLEXI_SAFETY_ROUNDS.OVERVIEW.OPEN_COUNT"
                  | translate: { count: round.OpenDeviationCount }
              }}
              <a class="fvr-link" [routerLink]="deviationsUrl">{{
                "FLEXI_SAFETY_ROUNDS.LIST.SEE_DEVIATIONS" | translate
              }}</a>
            </div>
          }
          <div class="fvr-summary__foot fvr-summary__foot--end">
            <button
              type="button"
              class="fas-btn fas-btn--ghost"
              [disabled]="busyActions.has(round.Id)"
              (click)="downloadReport(round)"
            >
              <i
                class="pi"
                [ngClass]="
                  busyActions.has(round.Id) ? 'pi-spin pi-spinner' : 'pi-file-pdf'
                "
                aria-hidden="true"
              ></i>
              {{ (busyActions.get(round.Id) || 'FLEXI_SAFETY_ROUNDS.DOWNLOAD_REPORT') | translate }}
            </button>
          </div>
        </article>
      }
    </div>
  }

  @if (!loading && !loadFailed) {
    <section class="fvr-overview" aria-labelledby="fvr-overview-title">
      <div class="fvr-overview__panel fvr-overview__history">
        <h3 id="fvr-overview-title" class="fvr-h3">
          {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.TITLE" | translate }}
        </h3>
        <p class="fas-muted">
          {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.SELECT_HINT" | translate }}
        </p>
        @if (completedRounds.length) {
          <form
            class="fvr-overview__filters"
            (ngSubmit)="applyOverviewFilters()"
          >
            <label
              >{{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.SEARCH" | translate }}
              <input
                type="search"
                name="overviewSearch"
                [ngModel]="overviewSearch"
                (ngModelChange)="overviewSearch = $event; applyOverviewFilters()"
              />
            </label>
            <label
              >{{ "FLEXI_SAFETY_ROUNDS.COLUMN.TEMPLATE" | translate }}
              <select
                name="overviewTemplate"
                [ngModel]="overviewTemplate"
                (ngModelChange)="overviewTemplate = $event; applyOverviewFilters()"
              >
                <option value="">
                  {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.ALL_TEMPLATES" | translate }}
                </option>
                @for (name of overviewTemplates; track name) {
                  <option [value]="name">{{ name }}</option>
                }
              </select>
            </label>
            <label
              >{{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.FROM" | translate }}
              <input
                type="date"
                name="overviewFrom"
                [ngModel]="overviewFrom"
                (ngModelChange)="overviewFrom = $event; applyOverviewFilters()"
              />
            </label>
            <label
              >{{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.TO" | translate }}
              <input
                type="date"
                name="overviewTo"
                [ngModel]="overviewTo"
                (ngModelChange)="overviewTo = $event; applyOverviewFilters()"
              />
            </label>
            <div class="fvr-overview__filter-toolbar">
              <div class="fvr-overview__filter-actions">
                <button
                  type="submit"
                  class="fas-btn fas-btn--primary"
                  [disabled]="invalidOverviewDates"
                >
                  {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.APPLY" | translate }}
                </button>
                <button
                  type="button"
                  class="fas-btn fas-btn--ghost"
                  (click)="resetOverviewFilters()"
                >
                  {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.CLEAR" | translate }}
                </button>
              </div>
              <div
                class="fvr-overview__pagination"
                aria-label="{{
                  'FLEXI_SAFETY_ROUNDS.OVERVIEW.PAGES' | translate
                }}"
              >
                <span aria-live="polite">{{
                  "FLEXI_SAFETY_ROUNDS.OVERVIEW.RANGE"
                    | translate
                      : {
                          first: overviewRangeStart,
                          last: overviewRangeEnd,
                          count: filteredRounds.length,
                          total: completedRounds.length
                        }
                }}</span>
                @if (filteredRounds.length > overviewPageSize) {
                  <div class="fvr-overview__filter-actions">
                    <button
                      type="button"
                      class="fas-btn fas-btn--ghost"
                      [disabled]="overviewPage === 1"
                      (click)="changeOverviewPage(overviewPage - 1)"
                    >
                      {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.NEWER" | translate }}
                    </button>
                    <button
                      type="button"
                      class="fas-btn fas-btn--ghost"
                      [disabled]="overviewRangeEnd === filteredRounds.length"
                      (click)="changeOverviewPage(overviewPage + 1)"
                    >
                      {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.OLDER" | translate }}
                    </button>
                  </div>
                }
              </div>
            </div>
          </form>
          @if (invalidOverviewDates) {
            <p class="fas-error" role="alert">
              {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.INVALID_DATES" | translate }}
            </p>
          }
          @if (!filteredRounds.length) {
            <p class="fas-muted">
              {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.NO_MATCHES" | translate }}
            </p>
          }
        }
        @if (visibleRounds.length) {
          <div class="fvr-overview__legend">
            @for (grade of findingGrades; track grade) {
              <span
                ><i
                  class="fvr-overview__swatch"
                  [style.background]="'var(--fvr-grade-' + grade + ')'"
                  aria-hidden="true"
                ></i
                >{{ grade }} ·
                {{ "FLEXI_SAFETY_ROUNDS.GRADE." + grade | translate }}</span
              >
            }
          </div>
          <div class="fvr-overview__rounds">
            @for (round of visibleRounds; track round.Id) {
              <button
                type="button"
                class="fvr-overview__round"
                [attr.aria-pressed]="selectedRound?.Id === round.Id"
                (click)="selectRound(round)"
              >
                <span class="fvr-overview__round-label"
                  ><strong>{{ round.RoundDate | date: "dd-MM-yyyy" }}</strong
                  ><span>{{ round.Location || round.Title }}</span></span
                >
                <span class="fvr-overview__bar">
                  @for (grade of findingGrades; track grade) {
                    @if (findingCount(round, grade); as count) {
                      <span
                        class="fvr-overview__segment"
                        [style.width.%]="chartPercent(count, maximumFindings)"
                        [style.background]="'var(--fvr-grade-' + grade + ')'"
                        [style.color]="'var(--fvr-grade-' + grade + '-ink)'"
                        [pTooltip]="
                          ('FLEXI_SAFETY_ROUNDS.GRADE.' + grade | translate) +
                          ': ' +
                          count
                        "
                      >
                        <span aria-hidden="true">{{ count }}</span
                        ><span class="fas-sr-only"
                          >{{
                            "FLEXI_SAFETY_ROUNDS.GRADE." + grade | translate
                          }}: {{ count }}</span
                        >
                      </span>
                    }
                  }
                  @if (!findingTotal(round)) {
                    <span class="fas-muted fvr-overview__zero">{{
                      "FLEXI_SAFETY_ROUNDS.LIST.NO_FINDINGS" | translate
                    }}</span>
                  }
                </span>
                <strong>{{ findingTotal(round) }}</strong>
              </button>
            }
          </div>
          <div class="fvr-overview__axis" aria-hidden="true">
            <span>0</span><span>{{ maximumFindings }}</span>
          </div>
          <p class="fvr-overview__axis-title fas-muted">
            {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.FINDING_COUNT" | translate }}
          </p>
          <p class="fvr-hint">
            {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.SCOPE_NOTE" | translate }}
          </p>
        } @else if (!completedRounds.length) {
          <p class="fas-muted">
            {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.NO_COMPLETED" | translate }}
          </p>
        }
      </div>
      @if (selectedRound; as selected) {
        <div class="fvr-overview__selection" aria-live="polite">
          <strong
            >{{ selected.RoundDate | date: "dd-MM-yyyy" }} ·
            {{ selected.Location || selected.Title }}</strong
          >
          <span class="fas-muted">{{
            selected.TemplateName ||
              ("FLEXI_SAFETY_ROUNDS.NO_TEMPLATE" | translate)
          }}</span>
        </div>
        @if (detailLoading) {
          <div class="fvr-overview__selection" aria-busy="true"><flexi-safety-round-skeleton /></div>
        }
        @if (detailFailed) {
          <div class="fvr-overview__selection" role="alert">
            <p>{{ "FLEXI_SAFETY_ROUNDS.LOAD_FAILED" | translate }}</p>
            <button
              type="button"
              class="fas-btn fas-btn--ghost"
              (click)="selectRound(selected)"
            >
              {{ "FLEXI_SAFETY_ROUNDS.ASSIGN.RETRY" | translate }}
            </button>
          </div>
        }
        @if (roundDetail; as detail) {
          <section
            class="fvr-overview__panel"
            aria-labelledby="fvr-assessment-title"
          >
            <h3 id="fvr-assessment-title" class="fvr-h3">
              {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.RESULTS" | translate }}
            </h3>
            <p class="fas-muted">
              {{
                "FLEXI_SAFETY_ROUNDS.ANSWERED_OF"
                  | translate
                    : { graded: assessedCount, total: detail.Items.length }
              }}
            </p>
            <div class="fvr-overview__distribution" aria-hidden="true">
              @for (grade of grades; track grade) {
                @if (gradeCount(grade); as count) {
                  <span
                    [style.width.%]="chartPercent(count, detail.Items.length)"
                    [style.background]="'var(--fvr-grade-' + grade + ')'"
                    [pTooltip]="
                      ('FLEXI_SAFETY_ROUNDS.GRADE.' + grade | translate) +
                      ': ' +
                      count
                    "
                  ></span>
                }
              }
              @if (gradeCount(null); as count) {
                <span
                  [style.width.%]="chartPercent(count, detail.Items.length)"
                  class="fvr-overview__unanswered"
                ></span>
              }
            </div>
            <dl class="fvr-overview__counts">
              @for (grade of grades; track grade) {
                <div>
                  <dt>
                    <i
                      class="fvr-overview__swatch"
                      [style.background]="'var(--fvr-grade-' + grade + ')'"
                      aria-hidden="true"
                    ></i
                    >{{ "FLEXI_SAFETY_ROUNDS.GRADE." + grade | translate }}
                  </dt>
                  <dd>{{ gradeCount(grade) }}</dd>
                </div>
              }
              @if (gradeCount(null); as count) {
                <div>
                  <dt>
                    {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.UNANSWERED" | translate }}
                  </dt>
                  <dd>{{ count }}</dd>
                </div>
              }
            </dl>
            <p class="fvr-hint">
              {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.NA_NOTE" | translate }}
            </p>
            <button
              type="button"
              class="fas-btn fas-btn--ghost"
              (click)="open(selected)"
            >
              {{ "FLEXI_SAFETY_ROUNDS.LIST.OPEN" | translate }}
            </button>
          </section>
          <section
            class="fvr-overview__panel"
            aria-labelledby="fvr-followup-title"
          >
            <h3 id="fvr-followup-title" class="fvr-h3">
              {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.FOLLOWUP" | translate }}
            </h3>
            <p class="fas-muted">
              {{
                "FLEXI_SAFETY_ROUNDS.OVERVIEW.LINKED_COUNT"
                  | translate: { count: linkedDeviations.length }
              }}
            </p>
            @for (
              key of ["NEEDS_FOLLOWUP", "DONE_CLOSED", "REJECTED"];
              track key;
              let index = $index
            ) {
              <div class="fvr-overview__followup-row">
                <span>{{
                  "FLEXI_SAFETY_ROUNDS.OVERVIEW." + key | translate
                }}</span>
                <span class="fvr-overview__track" aria-hidden="true"
                  ><span
                    [style.width.%]="
                      chartPercent(
                        followupCounts[index],
                        linkedDeviations.length
                      )
                    "
                  ></span
                ></span>
                <strong>{{ followupCounts[index] }}</strong>
              </div>
            }
            @if (overdueCount) {
              <p class="fvr-overview__overdue">
                {{
                  "FLEXI_SAFETY_ROUNDS.OVERVIEW.OVERDUE_COUNT"
                    | translate: { count: overdueCount }
                }}
              </p>
            }
            @if (!linkedDeviations.length) {
              <p class="fas-muted">
                {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.NO_LINKED" | translate }}
              </p>
            }
            <a class="fas-btn fas-btn--ghost" [routerLink]="deviationsUrl">{{
              "FLEXI_SAFETY_ROUNDS.LIST.SEE_DEVIATIONS" | translate
            }}</a>
          </section>
          @if (linkedDeviations.length) {
            <section
              class="fvr-overview__panel fvr-overview__linked"
              aria-labelledby="fvr-linked-title"
            >
              <h3 id="fvr-linked-title" class="fvr-h3">
                {{ "FLEXI_SAFETY_ROUNDS.OVERVIEW.LINKED_TITLE" | translate }}
              </h3>
              <ul class="fvr-overview__deviations">
                @for (deviation of linkedDeviations; track deviation.Id) {
                  <li>
                    <div>
                      <a
                        class="fvr-link"
                        [routerLink]="deviationUrl(deviation.Id)"
                        >{{ deviation.Number }} ·
                        {{ deviationTitle(deviation.Id) }}</a
                      >
                      <p class="fas-muted">
                        {{ "FLEXI_SAFETY_ROUNDS.DEVIATION.OWNER" | translate }}:
                        {{ deviation.ExecutionUserName || "—" }}
                      </p>
                    </div>
                    <div class="fvr-overview__deviation-status">
                      <span>{{
                        deviationStatusKey(deviation.Status) | translate
                      }}</span
                      ><span
                        [class.fvr-overview__overdue]="isOverdue(deviation)"
                        >{{
                          (isOverdue(deviation)
                            ? "FLEXI_SAFETY_ROUNDS.OVERVIEW.OVERDUE"
                            : "FLEXI_SAFETY_ROUNDS.DEVIATION.DEADLINE"
                          ) | translate
                        }}:
                        {{
                          (deviation.Deadline | date: "dd-MM-yyyy") || "—"
                        }}</span
                      >
                    </div>
                  </li>
                }
              </ul>
            </section>
          }
        }
      }
    </section>
  }

  <section class="fvr-list__history" *ngIf="!loading && !loadFailed">
    <h3 class="fvr-h3">{{ "FLEXI_SAFETY_ROUNDS.LIST.HISTORY" | translate }}</h3>

    <div class="fvr-table-card">
      <p-table
        [value]="rounds"
        dataKey="Id"
        [loading]="loading"
        [rows]="10"
        [paginator]="rounds.length > 10"
        [rowsPerPageOptions]="[10, 20, 50]"
        paginatorDropdownAppendTo="body"
        [rowHover]="true"
        styleClass="p-datatable-sm"
        [tableStyle]="{ 'min-width': '52rem' }"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>{{ "FLEXI_SAFETY_ROUNDS.COLUMN.DATE" | translate }}</th>
            <th>{{ "FLEXI_SAFETY_ROUNDS.COLUMN.LOCATION" | translate }}</th>
            <th>{{ "FLEXI_SAFETY_ROUNDS.COLUMN.TEMPLATE" | translate }}</th>
            <th>{{ "FLEXI_SAFETY_ROUNDS.COLUMN.RESPONSIBLE" | translate }}</th>
            <th>{{ "FLEXI_SAFETY_ROUNDS.COLUMN.FINDINGS" | translate }}</th>
            <th>{{ "FLEXI_SAFETY_ROUNDS.COLUMN.STATUS" | translate }}</th>
            <th class="fvr-list__menu-col">
              <span class="fas-sr-only">{{ "ACTIONS" | translate }}</span>
            </th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-round>
          <tr>
            <td class="fvr-num">
              <a
                class="fvr-link"
                href="javascript:void(0)"
                (click)="open(round)"
                >{{ round.RoundDate | date: "dd-MM-yyyy" }}</a
              >
            </td>
            <td>{{ round.Location || "—" }}</td>
            <td class="fas-muted">
              {{
                round.TemplateName ||
                  ("FLEXI_SAFETY_ROUNDS.NO_TEMPLATE" | translate)
              }}
            </td>
            <td>{{ round.ResponsibleUserName || "—" }}</td>
            <td>
              <div class="fvr-chips">
                @for (chip of findingChips(round); track chip.grade) {
                  <span
                    class="fvr-grade"
                    [ngClass]="'fvr-grade--' + chip.grade"
                    [pTooltip]="
                      'FLEXI_SAFETY_ROUNDS.FINDING_TOOLTIP'
                        | translate: { grade: chip.grade, count: chip.count }
                    "
                    [attr.aria-label]="
                      'FLEXI_SAFETY_ROUNDS.FINDING_TOOLTIP'
                        | translate: { grade: chip.grade, count: chip.count }
                    "
                    >{{ chip.count }}</span
                  >
                } @empty {
                  <span class="fvr-empty-cell">—</span>
                }
              </div>
            </td>
            <td>
              @if (round.Status === Status.Completed) {
                <span class="fvr-pill fvr-pill--done">{{
                  "FLEXI_SAFETY_ROUNDS.STATUS.COMPLETED" | translate
                }}</span>
              } @else {
                <span class="fvr-pill fvr-pill--progress">{{
                  "FLEXI_SAFETY_ROUNDS.STATUS.IN_PROGRESS" | translate
                }}</span>
              }
            </td>
            <td class="fvr-list__menu-col">
              @if (busyActions.has(round.Id)) {
                <span class="fas-sr-only" role="status">{{ busyActions.get(round.Id) | translate }}</span>
              }
              <button
                type="button"
                class="fas-icon-btn"
                [attr.aria-label]="
                  'FLEXI_SAFETY_ROUNDS.LIST.ROW_ACTIONS'
                    | translate
                      : { date: (round.RoundDate | date: 'dd-MM-yyyy') }
                "
                [disabled]="busyActions.has(round.Id)"
                (click)="openRowMenu($event, round)"
              >
                <i
                  class="pi"
                  [ngClass]="
                    busyActions.has(round.Id)
                      ? 'pi-spin pi-spinner'
                      : 'pi-ellipsis-v'
                  "
                  aria-hidden="true"
                ></i>
              </button>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="7">
              <div class="fvr-empty">
                <i class="pi pi-inbox" aria-hidden="true"></i>
                <p>{{ "FLEXI_SAFETY_ROUNDS.LIST.EMPTY" | translate }}</p>
                @if (canAuthor) {
                  <p class="fvr-hint">
                    {{ "FLEXI_SAFETY_ROUNDS.LIST.EMPTY_HINT" | translate }}
                  </p>
                  <button
                    type="button"
                    class="fas-btn fas-btn--primary"
                    [disabled]="loading || starting"
        (click)="openStart()"
                  >
                    <i class="pi pi-plus" aria-hidden="true"></i>
                    {{ "FLEXI_SAFETY_ROUNDS.LIST.NEW" | translate }}
                  </button>
                }
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </section>
</section>

<!-- One shared row menu; its model is rebuilt for the row that opened it. -->
<p-menu #rowMenu [popup]="true" [model]="rowMenuItems" appendTo="body"></p-menu>

<p-dialog
  [visible]="startOpen"
  (visibleChange)="$event ? null : closeStart()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="!starting"
  [style]="{ width: '600px', maxWidth: '95vw' }"
  [header]="'FLEXI_SAFETY_ROUNDS.START.TITLE' | translate"
>
  <div class="fvr-start">
    @if (inProgress; as round) {
      <p class="fas-note">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span>
          {{
            "FLEXI_SAFETY_ROUNDS.START.ALREADY_IN_PROGRESS"
              | translate
                : {
                    date: (round.RoundDate | date: "dd-MM-yyyy"),
                    location: round.Location || round.Title
                  }
          }}
          <button type="button" class="fvr-link" (click)="continueInProgress()">
            {{ "FLEXI_SAFETY_ROUNDS.START.CONTINUE_IT" | translate }}
          </button>
        </span>
      </p>
    }

    @if (templatesFailed) {
      <p class="fas-error" role="alert">
        {{ "FLEXI_SAFETY_ROUNDS.START.TEMPLATES_FAILED" | translate }}
      </p>
    }

    @if (templatesLoading) {
      <flexi-safety-round-skeleton />
    } @else {
      <fieldset class="fvr-start__list" [disabled]="starting">
        <legend class="fvr-start__legend">
          {{ "FLEXI_SAFETY_ROUNDS.START.PICK_TEMPLATE" | translate }}
        </legend>
        @for (template of templates; track template.Id) {
          <label
            class="fvr-start__option"
            [class.fvr-start__option--selected]="
              selectedTemplateId === template.Id
            "
          >
            <input
              type="radio"
              name="fvr-template"
              [value]="template.Id"
              [(ngModel)]="selectedTemplateId"
            />
            <span class="fvr-start__text">
              <span class="fvr-start__name">
                <strong>{{ template.Name }}</strong>
                @if (template.IsDefault) {
                  <span class="fas-badge fas-badge--primary">{{
                    "FLEXI_SAFETY_ROUNDS.START.BUSINESS_DEFAULT" | translate
                  }}</span>
                }
              </span>
              <span class="fas-muted">{{ templateMeta(template) }}</span>
            </span>
          </label>
        }
        <label
          class="fvr-start__option"
          [class.fvr-start__option--selected]="
            selectedTemplateId === noTemplate
          "
        >
          <input
            type="radio"
            name="fvr-template"
            [value]="noTemplate"
            [(ngModel)]="selectedTemplateId"
          />
          <span class="fvr-start__text">
            <strong>{{
              "FLEXI_SAFETY_ROUNDS.START.WITHOUT_TEMPLATE" | translate
            }}</strong>
            <span class="fas-muted">{{
              "FLEXI_SAFETY_ROUNDS.START.WITHOUT_TEMPLATE_HINT" | translate
            }}</span>
          </span>
        </label>
      </fieldset>
      <p class="fvr-hint">
        {{ "FLEXI_SAFETY_ROUNDS.START.DETAILS_HINT" | translate }}
      </p>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="fas-dialog-actions fvr-dialog-actions">
      <button
        type="button"
        class="fas-btn fas-btn--ghost"
        [disabled]="starting"
        (click)="closeStart()"
      >
        {{ "CANCEL" | translate }}
      </button>
      <button
        type="button"
        class="fas-btn fas-btn--primary"
        [disabled]="starting || templatesLoading || selectedTemplateId === null"
        (click)="start()"
      >
        @if (starting) {
          <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
        }
        {{ (starting ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.STARTING' : 'FLEXI_SAFETY_ROUNDS.START.SUBMIT') | translate }}
      </button>
    </div>
  </ng-template>
</p-dialog>

<p-confirmDialog
  key="fvrList"
  [style]="{ width: '480px', maxWidth: '95vw' }"
></p-confirmDialog>
